/**
 * Os valores que a consulta recebe, um por parâmetro, na ordem que a operação
 * declara. O de data se escolhe ("Hoje", "Amanhã", "Daqui a 7 dias"); os
 * outros são uma expressão sobre o evento, com o "Inserir".
 */

import { useState, type ReactNode } from 'react';
import {
  DIAS_RELATIVOS,
  diasDaExpressao,
  expressaoDaData,
  nomeDosDias,
} from '@/shared/lib/datas-em-expressao';
import { Botao, Campo, CampoSelecao } from '@/shared/ui';
import { ComCamposDoEvento } from './inserir-campo';

export function ParametrosDaConsulta({
  parametros,
  valores,
  campos,
  aoMudar,
}: {
  parametros: readonly { nome: string; tipo: string }[];
  valores: readonly string[];
  campos: readonly string[];
  aoMudar: (valores: string[]) => void;
}): ReactNode {
  /** As posições de data em que a pessoa pediu para escrever a expressão. */
  const [escrevendo, definirEscrevendo] = useState<ReadonlySet<number>>(() => new Set());
  if (parametros.length === 0) {
    return <p className="editor-do-mapa__painel-ajuda">Esta consulta não recebe valores.</p>;
  }
  const mudar = (i: number, valor: string): void =>
    aoMudar(parametros.map((_, j) => (j === i ? valor : (valores[j] ?? ''))));
  const alternar = (i: number, ligar: boolean): void =>
    definirEscrevendo((antes) => {
      const depois = new Set(antes);
      if (ligar) depois.add(i);
      else depois.delete(i);
      return depois;
    });

  return (
    <fieldset className="editor-do-mapa__condicao">
      <legend className="editor-do-mapa__condicao-titulo">O que a consulta recebe</legend>
      {parametros.map((p, i) => {
        const valor = valores[i] ?? '';
        const dias = diasDaExpressao(valor);
        const porEscolha =
          p.tipo === 'data' && !escrevendo.has(i) && (valor.trim() === '' || dias !== null);
        if (porEscolha) {
          return (
            <div key={p.nome} className="editor-do-mapa__condicao">
              <CampoSelecao
                rotulo={p.nome}
                value={dias === null ? '' : String(dias)}
                onChange={(e) =>
                  mudar(i, e.target.value === '' ? '' : expressaoDaData(Number(e.target.value)))
                }
              >
                <option value="">Escolha o dia</option>
                {DIAS_RELATIVOS.map((d) => (
                  <option key={d} value={String(d)}>
                    {nomeDosDias(d)}
                  </option>
                ))}
              </CampoSelecao>
              <Botao variante="fantasma" miudo onClick={() => alternar(i, true)}>
                Escrever a expressão
              </Botao>
            </div>
          );
        }
        return (
          <div key={p.nome} className="editor-do-mapa__condicao">
            <ComCamposDoEvento campos={campos} valor={valor} aoMudar={(v) => mudar(i, v)}>
              {(ref) => (
                <Campo
                  ref={ref}
                  rotulo={p.nome}
                  ajuda='Uma expressão sobre o evento. Texto fixo vai entre aspas, como "Vendas".'
                  spellCheck={false}
                  value={valor}
                  onChange={(e) => mudar(i, e.target.value)}
                />
              )}
            </ComCamposDoEvento>
            {p.tipo === 'data' && (
              <Botao
                variante="fantasma"
                miudo
                onClick={() => {
                  alternar(i, false);
                  if (diasDaExpressao(valor) === null) mudar(i, '');
                }}
              >
                Escolher o dia
              </Botao>
            )}
          </div>
        );
      })}
    </fieldset>
  );
}
